import React, { forwardRef, InputHTMLAttributes, Ref } from 'react'

import CustomSelect from '@/components/base/custom-select'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      resetKey?: string
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
    }
  >

const FormControl = forwardRef(
  (
    {
      id,
      type,
      label,
      checked,
      choices,
      placeholder,
      className,
      resetKey,
      children,
      error,
      ...props
    }: FormControlProps,
    ref: Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const Component = (type === 'textarea' ? 'textarea' : 'input') as any

    return (
      <div
        className={cn(className, {
          hidden: type === 'hidden'
        })}
      >
        {type === 'consent' ? (
          <label
            className={cn([
              'relative flex items-start',
              {
                'pointer-events-none opacity-40': props.disabled
              }
            ])}
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              className={cn([
                'size-[1.3rem] opacity-0',
                '[&:checked+*]:after:opacity-100 [&:focus+*]:before:opacity-100'
              ])}
              {...props}
            />
            <div
              className={cn([
                'absolute left-0 top-0 size-[1.3rem] rounded-full border border-dark dark:border-white',
                'before:absolute before:-left-[0.35rem] before:-top-[0.35rem] before:size-[1.8rem] before:rounded-full before:border-4 before:border-dark/15 before:opacity-0 dark:before:border-white/25',
                'after:absolute after:left-[0.2rem] after:top-[0.2rem] after:size-[0.7rem] after:rounded-full after:bg-dark after:opacity-0 dark:after:bg-white',
                {
                  'border-red-500 before:border-red-500/30': error,
                  'dark:border-red-500 dark:before:border-red-500/30': error
                }
              ])}
            />
            <Typography
              variant="breadcrumbs"
              className={cn([
                'ml-[1rem] font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
                'dark:text-white [&_a]:underline',
                {
                  '!text-red-500': error
                }
                /* 'text-text-secondary-dark mt-[0rem] max-w-[90%]',
                '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
                '[&>a:hover]:text-text-primary-dark [&>a]:underline',
                '[&>a]:transition-colors [&>a]:duration-300 [&>a]:ease-out-cubic',
                {
                  '!text-error': error
                } */
              ])}
            >
              {label}
            </Typography>
          </label>
        ) : type === 'select' ? (
          <CustomSelect
            ref={ref as Ref<HTMLButtonElement>}
            options={choices?.map((choice) => ({ value: choice.value, text: choice.text })) || []}
            placeholder={placeholder}
            onChange={props.onChange}
            onReset={() => props.onChange?.(null)}
            error={error}
            disabled={props.disabled}
            resetKey={resetKey}
          />
        ) : (
          <Component
            id={id}
            type={type}
            placeholder={placeholder}
            className={cn([
              'flex w-full appearance-none bg-transparent outline-none',
              'font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
              'rounded-[0.5rem] border border-dark/10 px-[1.5rem] py-[1.7rem] placeholder:text-dark dark:border-white/20 dark:placeholder:text-white',
              'focus:border-dark/30 focus:ring-2 focus:ring-dark/10 dark:focus:border-white/40 dark:focus:ring-white/20',
              { 'min-h-[10.4rem]': type === 'textarea' },
              {
                'border-red-500 focus:border-red-500 focus:ring-red-500/50': error,
                'dark:border-red-500 dark:focus:border-red-500 dark:focus:ring-red-500/50': error,
                'pointer-events-none opacity-40': props.disabled
              }
            ])}
            ref={type !== 'consent' && type !== 'select' ? ref : undefined}
            {...props}
          />
        )}
        {children}
      </div>
    )
  }
)

FormControl.displayName = 'FormControl'

export default FormControl
